// Two palettes, one shape. Dark is "Obsidian & Emerald" (warm charcoal, emerald accent); light is
// "Warm Ceramic & Indigo" (off-white, deep indigo accent). Amber marks agents in both. Every text colour
// is contrast-checked against the surfaces it sits on. Screens never import a palette directly: they call
// useTheme() / makeStyles(), so a switch re-skins everything.
import { createContext, useContext, useMemo } from 'react';
import { Platform, StyleSheet, useColorScheme } from 'react-native';
import { usePrefs } from './prefs';

const mono = Platform.select({ ios: 'Menlo', android: 'monospace', default: 'monospace' }) as string;

export type Theme = {
  name: 'dark' | 'light';
  mono: string;
  bg: string; surface: string; surface2: string; border: string;
  /** The desktop sidebar's own canvas, and what cards and inputs inside it sit on. */
  sidebar: string; sidebarSurface: string;
  ink: string; muted: string; faint: string;
  accent: string; accentInk: string; accentBg: string; accentBorder: string;
  /** `agent` fills (avatar, badge) and carries `agentInk`; `agentText` is the same amber made readable as text. */
  agent: string; agentText: string; agentInk: string; agentBg: string; agentBorder: string;
  good: string; approve: string; approveBg: string; approveBorder: string;
  danger: string; deny: string; denyBg: string; denyBorder: string;
  warn: string; backdrop: string;
  statusBar: 'light-content' | 'dark-content';
};

export const dark: Theme = {
  name: 'dark', mono,
  bg: '#111318', surface: '#181B22', surface2: '#222732', border: 'rgba(255,255,255,0.08)',
  sidebar: '#181B22', sidebarSurface: '#222732',
  ink: '#F1F5F9', muted: '#94A3B8', faint: '#7C8BA1',
  // White on emerald is 2.5:1, too faint to read, so buttons carry a near-black ink (7.4:1).
  accent: '#10B981', accentInk: '#04140E', accentBg: 'rgba(16,185,129,0.14)', accentBorder: 'rgba(16,185,129,0.40)',
  agent: '#f2b552', agentText: '#f2b552', agentInk: '#1a1300', agentBg: 'rgba(242,181,82,0.14)', agentBorder: 'rgba(242,181,82,0.35)',
  good: '#3ecf8e', approve: '#7ee38a', approveBg: 'rgba(62,207,142,0.14)', approveBorder: 'rgba(62,207,142,0.45)',
  danger: '#ff6b86', deny: '#ff8a9a', denyBg: 'rgba(255,107,134,0.14)', denyBorder: 'rgba(255,107,134,0.4)',
  warn: '#e5bd54', backdrop: 'rgba(0,0,0,0.55)',
  statusBar: 'light-content',
};

export const light: Theme = {
  name: 'light', mono,
  bg: '#F8FAFC', surface: '#FFFFFF', surface2: '#E2E8F0', border: 'rgba(15,23,42,0.10)',
  sidebar: '#F1F5F9', sidebarSurface: '#FFFFFF',
  ink: '#0F172A', muted: '#475569', faint: '#64748B',
  accent: '#4F46E5', accentInk: '#FFFFFF', accentBg: 'rgba(79,70,229,0.10)', accentBorder: 'rgba(79,70,229,0.35)',
  // The amber that fills an avatar is 2:1 as text on a light page; names and tags use the darker one (4.9:1).
  agent: '#e0a23a', agentText: '#A8590B', agentInk: '#1a1300', agentBg: 'rgba(224,162,58,0.16)', agentBorder: 'rgba(224,162,58,0.45)',
  good: '#1f9d5f', approve: '#15803D', approveBg: 'rgba(31,157,95,0.12)', approveBorder: 'rgba(31,157,95,0.40)',
  danger: '#d63a5a', deny: '#c9294b', denyBg: 'rgba(214,58,90,0.10)', denyBorder: 'rgba(214,58,90,0.35)',
  warn: '#8F5E00', backdrop: 'rgba(15,23,42,0.35)',
  statusBar: 'dark-content',
};

export const radius = { sm: 8, md: 12, lg: 16, xl: 20, pill: 999 };
export const space = (n: number) => n * 4;

/** A part of the page can sit on another canvas. The desktop sidebar uses it to re-point `bg` and `surface`
 *  for everything inside it, so the Home screen needs no change to be drawn there. Phones never set it. */
export const ThemeScope = createContext<((t: Theme) => Theme) | null>(null);
export const inSidebar = (t: Theme): Theme => ({ ...t, bg: t.sidebar, surface: t.sidebarSurface });

/** The active palette: Settings → Appearance (dark / light / system), resolved against the OS setting. */
export function useTheme(): Theme {
  const { theme } = usePrefs();
  const os = useColorScheme();
  const scope = useContext(ThemeScope);
  const mode = theme === 'system' ? (os === 'light' ? 'light' : 'dark') : theme;
  const base = mode === 'light' ? light : dark;
  return useMemo(() => (scope ? scope(base) : base), [scope, base]);
}

/** `const useStyles = makeStyles((theme) => StyleSheet.create({...}))` → `const s = useStyles()` in the component. */
export function makeStyles<T>(factory: (theme: Theme) => T): () => T {
  return function useStyles() {
    const theme = useTheme();
    return useMemo(() => factory(theme), [theme]);
  };
}
export { StyleSheet };

/** Web only: react-native-web paints the knob of a switched-on Switch teal unless told otherwise.
 *  Spread it on every Switch: `<Switch {...webSwitch} ... />`. Empty on phones. */
export const webSwitch: object = Platform.OS === 'web' ? { activeThumbColor: '#FFFFFF' } : {};
